import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { isInstalled, getAddress, sendPayment } from '@gemwallet/api';
import { useAuth } from './contexts/AuthContext';
import { APP_CONSTANTS, XRP_CONSTANTS, API_ENDPOINTS } from './constants';
import { Header, Card, Button, Input } from './components/ui';
import { tokens } from './styles/tokens';

// Map existing colors to design tokens for compatibility
const rippleBlue = tokens.colors.primary[900];
const rippleCyan = tokens.colors.accent[400];
const rippleWhite = tokens.colors.neutral[0];
const rippleCardBg = tokens.colors.dark[800];
const rippleCardAccent = `${tokens.colors.accent[500]}20`;

function decimalToAmerican(decimal) {
  if (!decimal || decimal === 0) return '';
  if (decimal >= 2) {
    return `+${Math.round((decimal - 1) * 100)}`;
  } else {
    return `-${Math.round(100 / (decimal - 1))}`;
  }
}

function calcPayout(amount, decimalOdds) {
  if (!amount || !decimalOdds) return '';
  return (parseFloat(amount) * decimalOdds).toFixed(2);
}

// Team logo mapping
const teamLogoMapping = {
  // American League East
  "New York Yankees": "yankees.png",
  "Boston Red Sox": "redSox.png",
  "Tampa Bay Rays": "rays.png",
  "Toronto Blue Jays": "blueJays.png",
  "Baltimore Orioles": "orioles.png",
  
  // American League Central  
  "Chicago White Sox": "whiteSox.png",
  "Detroit Tigers": "tigers.png",
  "Kansas City Royals": "royals.png",
  "Minnesota Twins": "twins.png",
  "Cleveland Guardians": "indians.png",
  
  // American League West
  "Houston Astros": "astros.png",
  "Los Angeles Angels": "angels.png",
  "Oakland Athletics": "athletics.png",
  "Seattle Mariners": "mariners.png",
  "Texas Rangers": "rangers.png",
  
  // National League East
  "Atlanta Braves": "braves.png",
  "New York Mets": "mets.png",
  "Philadelphia Phillies": "phillies.png",
  "Washington Nationals": "nationals.png",
  "Miami Marlins": "marlins.png",
  
  // National League Central
  "Chicago Cubs": "cubs.png",
  "Milwaukee Brewers": "brewers.png",
  "St. Louis Cardinals": "cardinals.png",
  "Pittsburgh Pirates": "pirates.png",
  "Cincinnati Reds": "reds.png",
  
  // National League West
  "Los Angeles Dodgers": "dodgers.png",
  "San Francisco Giants": "giants.png",
  "San Diego Padres": "padres.png",
  "Arizona Diamondbacks": "diamondbacks.png",
  "Colorado Rockies": "rockies.png"
};

const getTeamLogo = (teamName) => {
  const logoFile = teamLogoMapping[teamName];
  return logoFile ? `/logos/MLB/${logoFile}` : null;
};

// EPL team logo mapping
const eplTeamLogoMapping = {
  // Direct matches
  "Liverpool": "liverpool.png",
  "Bournemouth": "bournemouth.png", 
  "Aston Villa": "astonVilla.png",
  "Newcastle United": "newcastleUnited.png",
  "Fulham": "fulham.png",
  "Tottenham Hotspur": "tottenhamHotspur.png",
  "Burnley": "burnley.png",
  "West Ham United": "westHamUnited.png",
  "Manchester City": "manchesterCity.png",
  
  // Special cases
  "Brighton and Hove Albion": "brighton.png",
  "Wolverhampton Wanderers": "wolves.png", 
  "Sunderland": "sunderlandAfc.png",
  
  // Additional teams with logos available
  "Arsenal": "arsenal.png",
  "Chelsea": "chelsea.png", 
  "Manchester United": "manchesterUnited.png",
  "Leicester City": "leicesterCity.png",
  "Everton": "everton.png",
  "Crystal Palace": "crystalPalace.png",
  "Southampton": "southampton.png",
  
  // Historical/Championship teams (available but may not be in current EPL)
  "Cardiff City": "cardiffCity.png",
  "Huddersfield Town": "huddersfield.png",
  "Norwich City": "norwichCity.png",
  "Watford": "watford.png",
  "West Bromwich Albion": "westBromwichAlbion.png",
  "Stoke City": "stokeCity.png",
  "Swansea City": "swanseaCityAfc.png"
};

// NBA team logo mapping
const nbaTeamLogoMapping = {
  // Eastern Conference - Atlantic Division
  "Boston Celtics": "celtics.png",
  "Brooklyn Nets": "nets.png",
  "New York Knicks": "knicks.png",
  "Philadelphia 76ers": "76ers.png",
  "Toronto Raptors": "raptors.png",
  
  // Eastern Conference - Central Division
  "Chicago Bulls": "bulls.png",
  "Cleveland Cavaliers": "cavaliers.png",
  "Detroit Pistons": "pistons.png",
  "Indiana Pacers": "pacers.png",
  "Milwaukee Bucks": "bucks.png",
  
  // Eastern Conference - Southeast Division
  "Atlanta Hawks": "hawks.png",
  "Charlotte Hornets": "hornets.png",
  "Miami Heat": "heat.png",
  "Orlando Magic": "magic.png",
  "Washington Wizards": "wizards.png",
  
  // Western Conference - Northwest Division
  "Denver Nuggets": "nuggets.png",
  "Minnesota Timberwolves": "timberwolves.png",
  "Oklahoma City Thunder": "thunder.png",
  "Portland Trail Blazers": "trailBlazers.png",
  "Utah Jazz": "jazz.png",
  
  // Western Conference - Pacific Division
  "Golden State Warriors": "warriors.png",
  "Los Angeles Clippers": "clippers.png",
  "Los Angeles Lakers": "lakers.png",
  "Phoenix Suns": "suns.png",
  "Sacramento Kings": "kings.png",
  
  // Western Conference - Southwest Division
  "Dallas Mavericks": "mavericks.png",
  "Houston Rockets": "rockets.png",
  "Memphis Grizzlies": "grizzlies.png",
  "New Orleans Pelicans": "pelicans.png",
  "San Antonio Spurs": "spurs.png"
};

const getEplTeamLogo = (teamName) => {
  const logoFile = eplTeamLogoMapping[teamName];
  return logoFile ? `/logos/Premier_League/${logoFile}` : null;
};

const getNbaTeamLogo = (teamName) => {
  const logoFile = nbaTeamLogoMapping[teamName];
  return logoFile ? `/logos/NBA/${logoFile}` : null;
};

const getTeamInitials = (teamName) => {
  return teamName.split(' ').map(word => word[0]).join('').slice(0, 3);
};

function GameDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, loading: authLoading, getAccessToken, getUserAttributes, logout, userInfo } = useAuth();
  
  // Get game and sport from navigation state first, fallback to API fetch
  const [game, setGame] = useState(location.state?.game || null);
  const [sportType, setSportType] = useState(location.state?.sport || null);
  const [loading, setLoading] = useState(!location.state?.game);
  const [betType, setBetType] = useState('h2h');
  const [selectedOutcome, setSelectedOutcome] = useState(null);
  const [betAmount, setBetAmount] = useState('');
  const [placing, setPlacing] = useState(false);
  const [error, setError] = useState('');
  const [walletAddress, setWalletAddress] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [message, setMessage] = useState('');

  // Logout function
  const handleLogout = () => {
    logout();
    navigate('/login');
  };



  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, authLoading, navigate]);

  // Only fetch from API if game wasn't passed in navigation state
  useEffect(() => {
               if (!game) {
             // Determine API sport based on sportType
             let apiSport = 'baseball_mlb'; // default
             if (sportType === 'soccer_epl') {
               apiSport = 'soccer_epl';
             } else if (sportType === 'basketball_nba') {
               apiSport = 'basketball_nba';
             }
             fetch(`${APP_CONSTANTS.ODDS_API_BASE_URL}/sports/${apiSport}/odds/?apiKey=${APP_CONSTANTS.ODDS_API_KEY}&regions=us&markets=h2h,spreads,totals`)
        .then(res => res.json())
        .then(data => {
          console.log('API Response:', data);
          console.log('Looking for game ID:', id);
          
          const found = data.find(g => g.id === id) || data.find(g => String(g.id) === String(id));
          console.log('Found game:', found);
          
          setGame(found);
          // Set sport type based on API used if not already set
          if (!sportType) {
            setSportType(apiSport);
          }
          setLoading(false);
        })
        .catch((error) => {
          console.error('API Error:', error);
          setLoading(false);
        });
    }
  }, [id, game, sportType]);

  // Connect GemWallet - following exact documentation pattern
  function connectWallet() {
    setError('');
    setConnecting(true);

    console.log('Checking if GemWallet is installed...');

    // Follow exact documentation pattern - check isInstalled() first
    isInstalled().then((response) => {
      console.log('isInstalled response:', response);
      
      if (response.result.isInstalled) {
        console.log('GemWallet is installed, requesting address...');
        setError('Please approve the connection in GemWallet popup...');
        
        // Now call getAddress() - this should preserve user gesture since we're still in click context
        getAddress().then((addressResponse) => {
          console.log('GemWallet getAddress response:', addressResponse);
          
          if (addressResponse && addressResponse.type === 'response' && addressResponse.result) {
            const address = addressResponse.result.address;
            setWalletAddress(address);
            setError('');
            console.log('Successfully connected to GemWallet:', address);
          } else if (addressResponse && addressResponse.type === 'reject') {
            setError('Connection cancelled by user.');
          } else {
            console.error('Unexpected getAddress response:', addressResponse);
            setError('Unexpected response from GemWallet. Please try again.');
          }
          setConnecting(false);
        }).catch((error) => {
          console.error('getAddress error:', error);
          
          if (error.message?.includes('User rejected') || 
              error.message?.includes('rejected') || 
              error.message?.includes('denied') ||
              error.name === 'UserRejectedRequestError') {
            setError('Connection cancelled by user.');
          } else {
            setError('Failed to get address: ' + (error.message || 'Unknown error'));
          }
          setConnecting(false);
        });
      } else {
        console.log('GemWallet is not installed');
        setError('GemWallet extension not detected. Please install GemWallet from https://gemwallet.app and refresh the page.');
        setConnecting(false);
      }
    }).catch((error) => {
      console.error('isInstalled error:', error);
      setError('Error checking GemWallet installation: ' + (error.message || 'Please install GemWallet from https://gemwallet.app'));
      setConnecting(false);
    });
  }

  // Save bet to backend API
  async function saveBetToAPI(txHash, memo) {
    try {
      const accessToken = getAccessToken();
      
      if (!accessToken) {
        console.error('No access token available');
        throw new Error('Authentication required');
      }

      // Prepare comprehensive bet data
      const betData = {
        gameId: game.id,
        outcome: selectedOutcome.name,
        odds: selectedOutcome.price,
        amount: parseFloat(betAmount),
        xrpTransactionHash: txHash,
        gameDetails: {
          homeTeam: game.home_team,
          awayTeam: game.away_team,
          sport: game.sport_title || 'Soccer',
          commenceTime: game.commence_time,
          bookmaker: selectedOutcome.bookmaker || 'Unknown'
        },
        homeTeam: game.home_team,
        awayTeam: game.away_team,
        sport: game.sport_title || 'Soccer',
        gameDate: new Date(game.commence_time).toISOString().split('T')[0],
        gameTime: new Date(game.commence_time).toISOString(),
        memo: memo,
        walletAddress: walletAddress,
        oddsFormat: 'decimal',
        americanOdds: decimalToAmerican(selectedOutcome.price),
        betType: betType,
        placedAt: new Date().toISOString()
      };

      console.log('Saving bet to API:', betData);

      const response = await fetch(`${API_ENDPOINTS.USER_API}/bet`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify(betData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Bet API error:', {
          status: response.status,
          statusText: response.statusText,
          body: errorText,
        });
        throw new Error(`Failed to save bet: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      console.log('Bet saved successfully:', result);
      setMessage('Bet placed successfully! Good luck! 🍀');
      setSelectedOutcome(null); // Clear selected outcome after successful bet
      setBetAmount(''); // Clear bet amount after successful bet
      
    } catch (error) {
      console.error('Error saving bet to API:', error);
      // Don't throw the error - we don't want to prevent navigation if bet save fails
      // The XRP transaction was successful, which is the most important part
      console.warn('Bet was placed on blockchain but failed to save to database:', error.message);
      setMessage('Bet was placed on blockchain but failed to save to database: ' + error.message);
    }
  }

  // Place bet function using GemWallet
  async function placeBet() {
    if (!walletAddress) {
      setError('Please connect your wallet first.');
      return;
    }

    if (!selectedOutcome) {
      setError('Please select an outcome to bet on.');
      return;
    }

    if (!betAmount || parseFloat(betAmount) <= 0) {
      setError('Please enter a valid bet amount.');
      return;
    }

    setPlacing(true);
    setError('');
    setMessage(''); // Clear previous messages

    try {
      const amountInDrops = (parseFloat(betAmount) * 1000000).toString();
      const memo = `RippleBet | Game ID: ${game.id} | Prediction: ${selectedOutcome.name} | Odds: ${decimalToAmerican(selectedOutcome.price)} | Match: ${game.home_team} vs ${game.away_team} | Amount: ${betAmount} XRP`;
      
      setError('Please approve the transaction in GemWallet...');

      const payment = {
        destination: XRP_CONSTANTS.DESTINATION_ADDRESS,
        amount: amountInDrops,
        memos: [
          {
            memo: {
              memoData: Array.from(new TextEncoder().encode(memo))
                .map(byte => byte.toString(16).padStart(2, '0'))
                .join('')
            }
          }
        ]
      };

      console.log('Sending GemWallet payment:', payment);
      const result = await sendPayment(payment);

      console.log('Payment result:', result);

      if (result.type === 'response' && result.result) {
        const txHash = result.result.hash;
        console.log('Bet placed successfully:', txHash);
        
        // Save bet to backend API
        setError('Saving bet details...');
        await saveBetToAPI(txHash, memo);
        
        // Navigate back to home with success message
        navigate('/home', { 
          state: { 
            successMessage: 'Bet placed successfully! Good luck! 🍀', 
            fromBet: true 
          } 
        });
      } else if (result.type === 'reject') {
        setError('Transaction cancelled by user.');
      } else {
        setError('Transaction was not completed. Please try again.');
      }
    } catch (e) {
      console.error('Bet placement error:', e);
      if (e.message?.includes('User rejected') || e.message?.includes('rejected')) {
        setError('Transaction cancelled by user.');
      } else if (e.message?.includes('Insufficient')) {
        setError('Insufficient XRP balance.');
      } else {
        setError('Error placing bet: ' + (e.message || 'Unknown error'));
      }
    } finally {
      setPlacing(false);
    }
  }

  function handleSelectOutcome(outcome) {
    setSelectedOutcome(outcome);
  }


  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: tokens.colors.gradients.dark,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Card style={{ padding: tokens.spacing[8], textAlign: 'center' }}>
          <div className="skeleton" style={{
            width: '300px',
            height: '20px',
            borderRadius: tokens.borderRadius.md,
            marginBottom: tokens.spacing[4]
          }} />
          <div className="skeleton" style={{
            width: '200px',
            height: '16px',
            borderRadius: tokens.borderRadius.md
          }} />
        </Card>
      </div>
    );
  }
  
  if (!game) {
    return (
      <div style={{
        minHeight: '100vh',
        background: tokens.colors.gradients.dark,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Card style={{ padding: tokens.spacing[8], textAlign: 'center' }}>
          <h2 style={{
            color: tokens.colors.error[400],
            fontSize: tokens.typography.fontSize.xl,
            marginBottom: tokens.spacing[4]
          }}>
            Game not found
          </h2>
          <Button onClick={() => navigate('/home')} variant="primary">
            Return Home
          </Button>
        </Card>
      </div>
    );
  }

  const topBook = game.bookmakers?.[0] || null;
  const h2hMarket = topBook?.markets.find(m => m.key === 'h2h');
  const totalsMarket = topBook?.markets.find(m => m.key === 'totals');
  const spreadsMarket = topBook?.markets.find(m => m.key === 'spreads');
  const market = betType === 'h2h' ? h2hMarket : betType === 'spreads' ? spreadsMarket : totalsMarket;

  const menuItems = [
    { label: 'Home', href: '/home' },
    { label: 'Sports', href: '/sports' },
    { label: 'My Bets', href: '/my-bets' }
  ];

  return (
    <div style={{
      minHeight: '100vh',
      background: tokens.colors.gradients.dark,
      position: 'relative'
    }}>
      <Header
        logo="ELXR Sportsbook"
        user={userInfo ? {
          name: userInfo.given_name || userInfo.name,
          email: userInfo.email
        } : null}
        onLogout={handleLogout}
        menuItems={menuItems}
      />
      
      {/* Animated Background */}
      <div style={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        pointerEvents: 'none'
      }}>
        <div style={{
          position: 'absolute',
          top: '20%',
          left: '10%',
          width: '600px',
          height: '600px',
          background: `radial-gradient(circle, ${tokens.colors.accent[900]}22 0%, transparent 70%)`,
          filter: 'blur(40px)',
          animation: 'float 15s ease-in-out infinite'
        }} />
        <div style={{
          position: 'absolute',
          bottom: '10%',
          right: '10%',
          width: '500px',
          height: '500px',
          background: `radial-gradient(circle, ${tokens.colors.primary[900]}22 0%, transparent 70%)`,
          filter: 'blur(40px)',
          animation: 'float 20s ease-in-out infinite reverse'
        }} />
      </div>

      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        padding: `${tokens.spacing[8]} ${tokens.spacing[6]}`,
        position: 'relative',
        zIndex: 1
      }}>
        {/* Back Button */}
        <button
          onClick={() => navigate('/home')}
          style={{
            background: `${tokens.colors.dark[800]}66`,
            backdropFilter: 'blur(10px)',
            border: `1px solid ${tokens.colors.neutral[800]}`,
            color: tokens.colors.neutral[400],
            padding: `${tokens.spacing[2]} ${tokens.spacing[4]}`,
            borderRadius: tokens.borderRadius.lg,
            display: 'flex',
            alignItems: 'center',
            gap: tokens.spacing[2],
            marginBottom: tokens.spacing[8],
            cursor: 'pointer',
            transition: 'all 0.2s',
            fontWeight: tokens.typography.fontWeight.medium
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = tokens.colors.accent[400];
            e.currentTarget.style.borderColor = tokens.colors.accent[700];
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = tokens.colors.neutral[400];
            e.currentTarget.style.borderColor = tokens.colors.neutral[800];
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Games
        </button>

        {/* Main Content Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 400px',
          gap: tokens.spacing[8],
          alignItems: 'start'
        }}>
          {/* Game Info Section */}
          <div>
            {/* Match Header */}
            <div style={{
              background: `linear-gradient(135deg, ${tokens.colors.dark[800]}99 0%, ${tokens.colors.dark[900]}99 100%)`,
              backdropFilter: 'blur(20px)',
              border: `1px solid ${tokens.colors.neutral[800]}`,
              borderRadius: tokens.borderRadius['2xl'],
              padding: tokens.spacing[8],
              marginBottom: tokens.spacing[6],
              position: 'relative',
              overflow: 'hidden'
            }}>
              {/* Decorative accent */}
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background: tokens.colors.gradients.accent
              }} />

              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: tokens.spacing[4]
              }}>
                <div>
                  <p style={{
                    color: tokens.colors.neutral[400],
                    fontSize: tokens.typography.fontSize.sm,
                    marginBottom: tokens.spacing[1]
                  }}>
                    {game.sport_title}
                  </p>
                  <p style={{
                    color: tokens.colors.accent[400],
                    fontSize: tokens.typography.fontSize.sm,
                    fontWeight: tokens.typography.fontWeight.medium
                  }}>
                    {new Date(game.commence_time).toLocaleString('en-US', {
                      weekday: 'long',
                      month: 'long',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
                <div style={{
                  padding: `${tokens.spacing[2]} ${tokens.spacing[4]}`,
                  background: game.has_started ? 
                    `${tokens.colors.error[900]}33` : 
                    `${tokens.colors.accent[900]}33`,
                  border: `1px solid ${game.has_started ? 
                    tokens.colors.error[700] : 
                    tokens.colors.accent[700]}`,
                  borderRadius: tokens.borderRadius.full,
                  fontSize: tokens.typography.fontSize.sm,
                  fontWeight: tokens.typography.fontWeight.medium,
                  color: game.has_started ? 
                    tokens.colors.error[400] : 
                    tokens.colors.accent[400]
                }}>
                  {game.has_started ? 'Live' : 'Upcoming'}
                </div>
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr auto 1fr',
                alignItems: 'center',
                gap: tokens.spacing[6]
              }}>
                {/* Home Team */}
                <div style={{ textAlign: 'center' }}>
                  <div style={{
                    width: '100px',
                    height: '100px',
                    borderRadius: tokens.borderRadius.lg,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto',
                    marginBottom: tokens.spacing[4],
                    fontSize: '3rem'
                  }}>
                    {((sportType === 'MLB' && getTeamLogo(game.home_team)) || (sportType === 'soccer_epl' && getEplTeamLogo(game.home_team)) || (sportType === 'basketball_nba' && getNbaTeamLogo(game.home_team))) ? (
                      <img
                        src={sportType === 'MLB' ? getTeamLogo(game.home_team) : sportType === 'soccer_epl' ? getEplTeamLogo(game.home_team) : getNbaTeamLogo(game.home_team)}
                        alt={game.home_team}
                        style={{
                          width: '100px',
                          height: '100px',
                          objectFit: 'contain'
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div style={{
                      display: ((sportType === 'MLB' && getTeamLogo(game.home_team)) || (sportType === 'soccer_epl' && getEplTeamLogo(game.home_team)) || (sportType === 'basketball_nba' && getNbaTeamLogo(game.home_team))) ? 'none' : 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '100%',
                      height: '100%',
                      fontSize: '3rem',
                      background: tokens.colors.gradients.card,
                      border: `2px solid ${tokens.colors.neutral[700]}`,
                      borderRadius: tokens.borderRadius.lg
                    }}>
                      🏠
                    </div>
                  </div>
                  <h2 style={{
                    fontSize: tokens.typography.fontSize['2xl'],
                    fontWeight: tokens.typography.fontWeight.bold,
                    color: tokens.colors.neutral[0],
                    marginBottom: tokens.spacing[2]
                  }}>
                    {game.home_team}
                  </h2>
                  <p style={{
                    color: tokens.colors.neutral[500],
                    fontSize: tokens.typography.fontSize.sm
                  }}>
                    Home
                  </p>
                </div>

                {/* VS */}
                <div style={{
                  fontSize: tokens.typography.fontSize['3xl'],
                  fontWeight: tokens.typography.fontWeight.bold,
                  color: tokens.colors.neutral[600],
                  padding: `${tokens.spacing[4]} ${tokens.spacing[6]}`
                }}>
                  VS
                </div>

                {/* Away Team */}
                <div style={{ textAlign: 'center' }}>
                  <div style={{
                    width: '100px',
                    height: '100px',
                    borderRadius: tokens.borderRadius.lg,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto',
                    marginBottom: tokens.spacing[4],
                    fontSize: '3rem'
                  }}>
                    {((sportType === 'MLB' && getTeamLogo(game.away_team)) || (sportType === 'soccer_epl' && getEplTeamLogo(game.away_team)) || (sportType === 'basketball_nba' && getNbaTeamLogo(game.away_team))) ? (
                      <img
                        src={sportType === 'MLB' ? getTeamLogo(game.away_team) : sportType === 'soccer_epl' ? getEplTeamLogo(game.away_team) : getNbaTeamLogo(game.away_team)}
                        alt={game.away_team}
                        style={{
                          width: '100px',
                          height: '100px',
                          objectFit: 'contain'
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div style={{
                      display: ((sportType === 'MLB' && getTeamLogo(game.away_team)) || (sportType === 'soccer_epl' && getEplTeamLogo(game.away_team)) || (sportType === 'basketball_nba' && getNbaTeamLogo(game.away_team))) ? 'none' : 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '100%',
                      height: '100%',
                      fontSize: '3rem',
                      background: tokens.colors.gradients.card,
                      border: `2px solid ${tokens.colors.neutral[700]}`,
                      borderRadius: tokens.borderRadius.lg
                    }}>
                      ✈️
                    </div>
                  </div>
                  <h2 style={{
                    fontSize: tokens.typography.fontSize['2xl'],
                    fontWeight: tokens.typography.fontWeight.bold,
                    color: tokens.colors.neutral[0],
                    marginBottom: tokens.spacing[2]
                  }}>
                    {game.away_team}
                  </h2>
                  <p style={{
                    color: tokens.colors.neutral[500],
                    fontSize: tokens.typography.fontSize.sm
                  }}>
                    Away
                  </p>
                </div>
              </div>
            </div>

            {/* Betting Markets */}
            <div style={{
              background: `linear-gradient(135deg, ${tokens.colors.dark[800]}99 0%, ${tokens.colors.dark[900]}99 100%)`,
              backdropFilter: 'blur(20px)',
              border: `1px solid ${tokens.colors.neutral[800]}`,
              borderRadius: tokens.borderRadius['2xl'],
              padding: tokens.spacing[6]
            }}>
              <h3 style={{
                fontSize: tokens.typography.fontSize.xl,
                fontWeight: tokens.typography.fontWeight.bold,
                color: tokens.colors.neutral[0],
                marginBottom: tokens.spacing[6]
              }}>
                Betting Markets
              </h3>

              {/* Market Type Tabs */}
              <div style={{
                display: 'flex',
                gap: tokens.spacing[2],
                marginBottom: tokens.spacing[6]
              }}>
                <button
                  onClick={() => {
                    setBetType('h2h');
                    setSelectedOutcome(null);
                  }}
                  style={{
                    background: betType === 'h2h' ? tokens.colors.accent[900] : 'transparent',
                    border: `1px solid ${betType === 'h2h' ? tokens.colors.accent[700] : tokens.colors.neutral[700]}`,
                    color: betType === 'h2h' ? tokens.colors.accent[400] : tokens.colors.neutral[400],
                    padding: `${tokens.spacing[2]} ${tokens.spacing[4]}`,
                    borderRadius: tokens.borderRadius.lg,
                    fontWeight: tokens.typography.fontWeight.medium,
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  Game Winner
                </button>
                
                {/* Only show Spread for MLB/Baseball games */}
                {sportType !== 'soccer_epl' && (
                  <button
                    onClick={() => {
                      setBetType('spreads');
                      setSelectedOutcome(null);
                    }}
                    style={{
                      background: betType === 'spreads' ? tokens.colors.accent[900] : 'transparent',
                      border: `1px solid ${betType === 'spreads' ? tokens.colors.accent[700] : tokens.colors.neutral[700]}`,
                      color: betType === 'spreads' ? tokens.colors.accent[400] : tokens.colors.neutral[400],
                      padding: `${tokens.spacing[2]} ${tokens.spacing[4]}`,
                      borderRadius: tokens.borderRadius.lg,
                      fontWeight: tokens.typography.fontWeight.medium,
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    Spread
                  </button>
                )}
                
                <button
                  onClick={() => {
                    setBetType('totals');
                    setSelectedOutcome(null);
                  }}
                  style={{
                    background: betType === 'totals' ? tokens.colors.accent[900] : 'transparent',
                    border: `1px solid ${betType === 'totals' ? tokens.colors.accent[700] : tokens.colors.neutral[700]}`,
                    color: betType === 'totals' ? tokens.colors.accent[400] : tokens.colors.neutral[400],
                    padding: `${tokens.spacing[2]} ${tokens.spacing[4]}`,
                    borderRadius: tokens.borderRadius.lg,
                    fontWeight: tokens.typography.fontWeight.medium,
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  {sportType === 'soccer_epl' ? 'Totals' : 'Total Runs'}
                </button>
              </div>

              {/* Odds Grid */}
              {market && (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: betType === 'h2h' ? `repeat(${market.outcomes.length}, 1fr)` : 'repeat(2, 1fr)',
                  gap: tokens.spacing[4]
                }}>
                  {/* Sort outcomes to put home team first, then away team, then others */}
                  {market.outcomes
                    .sort((a, b) => {
                      if (a.name === game.home_team) return -1;
                      if (b.name === game.home_team) return 1;
                      if (a.name === game.away_team) return -1;
                      if (b.name === game.away_team) return 1;
                      return 0;
                    })
                    .map((outcome, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleSelectOutcome(outcome)}
                      style={{
                        background: selectedOutcome?.name === outcome.name ? 
                          tokens.colors.gradients.accent : 
                          `${tokens.colors.dark[700]}66`,
                        border: `2px solid ${selectedOutcome?.name === outcome.name ? 
                          tokens.colors.accent[500] : 
                          tokens.colors.neutral[700]}`,
                        borderRadius: tokens.borderRadius.lg,
                        padding: tokens.spacing[4],
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        position: 'relative',
                        overflow: 'hidden'
                      }}
                      onMouseEnter={(e) => {
                        if (selectedOutcome?.name !== outcome.name) {
                          e.currentTarget.style.borderColor = tokens.colors.accent[600];
                          e.currentTarget.style.transform = 'translateY(-2px)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedOutcome?.name !== outcome.name) {
                          e.currentTarget.style.borderColor = tokens.colors.neutral[700];
                          e.currentTarget.style.transform = 'translateY(0)';
                        }
                      }}
                    >
                      <p style={{
                        color: selectedOutcome?.name === outcome.name ? 
                          tokens.colors.dark[900] : 
                          tokens.colors.neutral[300],
                        fontSize: tokens.typography.fontSize.sm,
                        marginBottom: tokens.spacing[2],
                        fontWeight: tokens.typography.fontWeight.medium
                      }}>
                        {betType === 'totals' 
                          ? `${outcome.name} ${outcome.point}` 
                          : betType === 'spreads' 
                            ? `${outcome.name} ${outcome.point > 0 ? `+${outcome.point}` : outcome.point}`
                            : outcome.name
                        }
                      </p>
                      <p style={{
                        color: selectedOutcome?.name === outcome.name ? 
                          tokens.colors.dark[900] : 
                          tokens.colors.accent[400],
                        fontSize: tokens.typography.fontSize['2xl'],
                        fontWeight: tokens.typography.fontWeight.bold
                      }}>
                        {decimalToAmerican(outcome.price)}
                      </p>
                    </button>
                  ))}
                </div>
              )}

              {!market && (
                <div style={{
                  textAlign: 'center',
                  padding: tokens.spacing[8],
                  color: tokens.colors.neutral[500]
                }}>
                  <p>No odds available for this market</p>
                </div>
              )}
            </div>
          </div>

          {/* Bet Slip Sidebar */}
          <div style={{
            position: 'sticky',
            top: tokens.spacing[8]
          }}>
            <div style={{
              background: `linear-gradient(135deg, ${tokens.colors.dark[800]}AA 0%, ${tokens.colors.dark[900]}DD 100%)`,
              backdropFilter: 'blur(16px)',
              border: `1px solid ${tokens.colors.neutral[700]}`,
              borderRadius: tokens.borderRadius['2xl'],
              padding: tokens.spacing[6],
              minHeight: '400px',
              boxShadow: `0 8px 32px rgba(0, 0, 0, 0.4)`
            }}>
              {/* Header */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: tokens.spacing[3],
                marginBottom: tokens.spacing[6],
                paddingBottom: tokens.spacing[4],
                borderBottom: `1px solid ${tokens.colors.neutral[800]}`
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  background: tokens.colors.gradients.accent,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.2rem'
                }}>
                  🎫
                </div>
                <h3 style={{
                  fontSize: tokens.typography.fontSize.xl,
                  fontWeight: tokens.typography.fontWeight.bold,
                  color: tokens.colors.neutral[0],
                  margin: 0
                }}>
                  Bet Slip
                </h3>
              </div>

              {selectedOutcome ? (
                <>
                  {/* Selected Bet Card */}
                  <div style={{
                    background: `${tokens.colors.dark[700]}88`,
                    border: `1px solid ${tokens.colors.accent[700]}`,
                    borderRadius: tokens.borderRadius.lg,
                    padding: tokens.spacing[4],
                    marginBottom: tokens.spacing[6]
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: tokens.spacing[3]
                    }}>
                      <div>
                        <p style={{
                          color: tokens.colors.neutral[400],
                          fontSize: tokens.typography.fontSize.sm,
                          margin: 0,
                          marginBottom: tokens.spacing[1]
                        }}>
                          {game.home_team} vs {game.away_team}
                        </p>
                        <p style={{
                          color: tokens.colors.neutral[0],
                          fontSize: tokens.typography.fontSize.base,
                          fontWeight: tokens.typography.fontWeight.semibold,
                          margin: 0
                        }}>
                          {betType === 'totals' 
                            ? `${selectedOutcome.name} ${selectedOutcome.point}` 
                            : betType === 'spreads' 
                              ? `${selectedOutcome.name} ${selectedOutcome.point > 0 ? `+${selectedOutcome.point}` : selectedOutcome.point}`
                              : selectedOutcome.name
                          }
                        </p>
                      </div>
                      <div style={{
                        background: tokens.colors.gradients.accent,
                        padding: `${tokens.spacing[2]} ${tokens.spacing[3]}`,
                        borderRadius: tokens.borderRadius.md,
                        color: tokens.colors.dark[900],
                        fontSize: tokens.typography.fontSize.lg,
                        fontWeight: tokens.typography.fontWeight.bold
                      }}>
                        {decimalToAmerican(selectedOutcome.price)}
                      </div>
                    </div>
                  </div>

                  {/* Bet Amount Input */}
                  <div style={{ marginBottom: tokens.spacing[6] }}>
                    <label style={{
                      display: 'block',
                      color: tokens.colors.neutral[300],
                      fontSize: tokens.typography.fontSize.sm,
                      marginBottom: tokens.spacing[2],
                      fontWeight: tokens.typography.fontWeight.medium
                    }}>
                      Bet Amount (XRP)
                    </label>
                    <Input
                      type="number"
                      value={betAmount}
                      onChange={(e) => setBetAmount(e.target.value)}
                      placeholder="Enter amount"
                      min="0.1"
                      max="10000"
                      step="0.1"
                      style={{
                        background: `${tokens.colors.dark[900]}88`,
                        borderColor: tokens.colors.neutral[700],
                        color: tokens.colors.neutral[0],
                        fontSize: tokens.typography.fontSize.lg,
                        height: '56px'
                      }}
                    />
                  </div>

                  {/* Payout Breakdown */}
                  {betAmount && (
                    <div style={{
                      background: `${tokens.colors.accent[900]}22`,
                      border: `1px solid ${tokens.colors.accent[700]}`,
                      borderRadius: tokens.borderRadius.lg,
                      padding: tokens.spacing[4],
                      marginBottom: tokens.spacing[6]
                    }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginBottom: tokens.spacing[2]
                      }}>
                        <span style={{ 
                          color: tokens.colors.neutral[400],
                          fontSize: tokens.typography.fontSize.sm
                        }}>
                          Potential Payout
                        </span>
                        <span style={{
                          color: tokens.colors.accent[400],
                          fontWeight: tokens.typography.fontWeight.bold
                        }}>
                          {(parseFloat(betAmount) * selectedOutcome.price).toFixed(2)} XRP
                        </span>
                      </div>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between'
                      }}>
                        <span style={{ 
                          color: tokens.colors.neutral[400],
                          fontSize: tokens.typography.fontSize.sm
                        }}>
                          Potential Profit
                        </span>
                        <span style={{
                          color: tokens.colors.accent[400],
                          fontWeight: tokens.typography.fontWeight.bold
                        }}>
                          +{((parseFloat(betAmount) * selectedOutcome.price) - parseFloat(betAmount)).toFixed(2)} XRP
                        </span>
                      </div>
                    </div>
                  )}

                  {!walletAddress && (
                    <Button
                      variant="secondary"
                      size="large"
                      onClick={connectWallet}
                      disabled={connecting}
                      style={{
                        width: '100%',
                        marginBottom: tokens.spacing[4],
                        background: `${tokens.colors.neutral[800]}`,
                        borderColor: tokens.colors.neutral[600],
                        color: tokens.colors.neutral[300]
                      }}
                    >
                      {connecting ? 'Connecting...' : 'Connect Wallet'}
                    </Button>
                  )}

                  {/* Place Bet Button */}
                  <Button
                    variant="primary"
                    size="large"
                    onClick={placeBet}
                    disabled={placing || !betAmount || parseFloat(betAmount) <= 0 || !walletAddress}
                    style={{
                      width: '100%',
                      background: tokens.colors.gradients.accent,
                      border: 'none',
                      fontSize: tokens.typography.fontSize.base,
                      fontWeight: tokens.typography.fontWeight.bold,
                      padding: `${tokens.spacing[4]} ${tokens.spacing[6]}`,
                      opacity: (placing || !betAmount || parseFloat(betAmount) <= 0 || !walletAddress) ? 0.5 : 1
                    }}
                  >
                    {placing ? 'Placing Bet...' : 'Place Bet'}
                  </Button>

                  {/* Messages */}
                  {error && (
                    <div style={{
                      marginTop: tokens.spacing[4],
                      padding: tokens.spacing[3],
                      background: `${tokens.colors.error[900]}33`,
                      border: `1px solid ${tokens.colors.error[700]}`,
                      borderRadius: tokens.borderRadius.md,
                      color: tokens.colors.error[400],
                      fontSize: tokens.typography.fontSize.sm,
                      textAlign: 'center'
                    }}>
                      {error}
                    </div>
                  )}

                  {message && (
                    <div style={{
                      marginTop: tokens.spacing[4],
                      padding: tokens.spacing[3],
                      background: message.includes('Successfully') || message.includes('🍀')
                        ? `${tokens.colors.accent[900]}33`
                        : `${tokens.colors.error[900]}33`,
                      border: `1px solid ${message.includes('Successfully') || message.includes('🍀')
                        ? tokens.colors.accent[700] 
                        : tokens.colors.error[700]}`,
                      borderRadius: tokens.borderRadius.md,
                      color: message.includes('Successfully') || message.includes('🍀')
                        ? tokens.colors.accent[400] 
                        : tokens.colors.error[400],
                      fontSize: tokens.typography.fontSize.sm,
                      textAlign: 'center'
                    }}>
                      {message}
                    </div>
                  )}
                </>
              ) : (
                /* Empty State */
                <div style={{
                  textAlign: 'center',
                  padding: tokens.spacing[8],
                  color: tokens.colors.neutral[500]
                }}>
                  <div style={{
                    fontSize: '3rem',
                    marginBottom: tokens.spacing[4],
                    opacity: 0.5
                  }}>
                    🎯
                  </div>
                  <h4 style={{
                    color: tokens.colors.neutral[300],
                    fontSize: tokens.typography.fontSize.lg,
                    fontWeight: tokens.typography.fontWeight.semibold,
                    marginBottom: tokens.spacing[2],
                    margin: 0
                  }}>
                    Ready to bet?
                  </h4>
                  <p style={{
                    fontSize: tokens.typography.fontSize.sm,
                    lineHeight: 1.5,
                    margin: 0
                  }}>
                    Select an outcome from the betting options to get started
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced animations */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(30px, -30px) scale(1.05); }
          66% { transform: translate(-20px, 20px) scale(0.95); }
        }
        
        @keyframes borderPulse {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 1; }
        }
        
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.8; transform: scale(1.05); }
        }
        
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
    </div>
  );
}

export default GameDetailPage;
